// AddRoleCard — carte permettant à un utilisateur connecté d'ajouter un nouveau
// rôle à son compte unique (ex: client qui veut devenir aussi livreur ou artisan).
// Activation immédiate (choix produit WÔMA, pas de validation admin).

import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Truck, Store, Palette, Plus, Loader2, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AVAILABLE_ROLES = [
  { role: "courier", label: "Livreur / Chauffeur", icon: Truck, color: "#D4AF37",
    description: "Accepter des courses et livraisons dans votre zone" },
  { role: "partner", label: "Partenaire Boutique", icon: Store, color: "#10B981",
    description: "Vendre vos produits via la plateforme" },
  { role: "artisan", label: "Artisan", icon: Palette, color: "#F472B6",
    description: "Proposer vos services artisanaux (couture, cordonnerie, etc.)" },
];

export default function AddRoleCard({ user, onUserUpdate }) {
  const [adding, setAdding] = useState(null); // role en cours d'ajout

  const existingRoles = new Set(
    (Array.isArray(user?.roles) ? user.roles : [user?.role]).filter(Boolean),
  );
  const missing = AVAILABLE_ROLES.filter((r) => !existingRoles.has(r.role));

  if (!user?.id || missing.length === 0) {
    return null;  // utilisateur a déjà tous les rôles
  }

  const handleAdd = async (role) => {
    setAdding(role);
    try {
      const res = await axios.post(`${API}/users/add-role`, {
        user_id: user.id,
        role,
      });
      const updated = res.data?.user;
      if (!updated) throw new Error("Réponse invalide");
      const merged = { ...user, ...updated, role: updated.active_role || role };
      if (onUserUpdate) onUserUpdate(merged);
      try { localStorage.setItem("user", JSON.stringify(merged)); } catch (e) { /* noop */ }
      toast.success(`Rôle ${role} activé — vous pouvez basculer depuis le menu en haut`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'ajouter ce rôle");
    } finally {
      setAdding(null);
    }
  };

  return (
    <section
      className="bg-[#0B1E2B] border border-white/10 rounded-[2rem] p-5 md:p-6"
      data-testid="add-role-card"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
          <Plus className="w-5 h-5 text-[#D4AF37]" />
        </div>
        <div>
          <h3 className="text-base font-black uppercase tracking-tight text-white">
            Devenir aussi…
          </h3>
          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
            Ajoutez un rôle à votre compte · Activation immédiate
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {missing.map((r) => {
          const Icon = r.icon;
          const busy = adding === r.role;
          return (
            <button
              key={r.role}
              type="button"
              onClick={() => handleAdd(r.role)}
              disabled={!!adding}
              className="group flex items-start gap-3 bg-black/40 hover:bg-black/60 border border-white/10 hover:border-[#D4AF37]/50 rounded-2xl p-4 text-left transition-all disabled:opacity-50"
              data-testid={`add-role-${r.role}-btn`}
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: `${r.color}1a`, color: r.color }}
              >
                {busy ? <Loader2 size={18} className="animate-spin" /> : <Icon size={18} />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-black text-white uppercase tracking-tight">
                  Devenir {r.label}
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5 leading-snug">
                  {r.description}
                </p>
              </div>
            </button>
          );
        })}
      </div>

      {existingRoles.size > 1 && (
        <p className="mt-4 text-[10px] text-emerald-400 font-bold uppercase tracking-widest flex items-center gap-2">
          <Check size={12} />
          {existingRoles.size} rôle{existingRoles.size > 1 ? "s" : ""} actif{existingRoles.size > 1 ? "s" : ""} sur votre compte
        </p>
      )}
    </section>
  );
}
